import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, PlusCircle, Trash2, MapPin, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function Sites() {
  const { t } = useI18n();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sites");
      setSites(data.sites);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/sites/${id}`);
      toast.success(t("common.success"));
      setSites(sites.filter((s) => s.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="sites-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1A2F24] mb-1">
            {t("sites.title")}
          </h1>
          <p className="text-[#5C6B61]">{t("sites.subtitle")}</p>
        </div>
        <Button data-testid="new-site-button" onClick={() => nav("/app/query")} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
          <PlusCircle size={16} className="mr-2" /> {t("sites.new")}
        </Button>
      </div>

      {loading ? (
        <div className="text-[#5C6B61] text-sm">{t("common.loading")}</div>
      ) : sites.length === 0 ? (
        <div className="bg-white border border-[#E5E2D9] rounded-xl p-12 text-center">
          <Building2 size={36} className="mx-auto text-[#5C6B61] mb-3" />
          <div className="text-[#5C6B61] mb-5">{t("sites.no_sites")}</div>
          <Button data-testid="sites-empty-cta" onClick={() => nav("/app/query")} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
            <PlusCircle size={16} className="mr-2" /> {t("sites.new")}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map((s) => (
            <div key={s.id} data-testid={`site-card-${s.id}`} className="bg-white border border-[#E5E2D9] rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-md bg-[#F1EFE7] text-[#1A2F24]">
                  <MapPin size={18} />
                </div>
                <div className="flex items-center gap-1">
                  {s.share_enabled && (
                    <span
                      data-testid={`shared-badge-${s.id}`}
                      title="Öffentlich geteilt"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#C05A44] text-white px-2 py-0.5 rounded-full font-medium"
                    >
                      <Share2 size={10} /> Geteilt
                    </span>
                  )}
                  <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-site-${s.id}`} className="text-[#5C6B61] hover:text-[#C05A44] p-1 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("sites.confirm_delete")}</AlertDialogTitle>
                      <AlertDialogDescription>{s.name}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction data-testid={`confirm-delete-${s.id}`} onClick={() => del(s.id)} className="bg-[#C05A44] hover:bg-[#A64B38]">
                        {t("sites.delete")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                </div>
              </div>
              <div className="font-display font-semibold text-lg text-[#1A2F24] mb-1 truncate">{s.name}</div>
              <div className="text-xs text-[#5C6B61] mb-4 line-clamp-2 min-h-[2rem]">
                {s.address || `${s.latitude?.toFixed(4)}, ${s.longitude?.toFixed(4)}`}
              </div>
              <div className="mt-auto pt-3 border-t border-[#E5E2D9] flex items-center justify-between">
                <span className="text-[10px] text-[#5C6B61]">
                  {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                </span>
                <Link to={`/app/sites/${s.id}`} className="text-sm font-medium text-[#1A2F24] hover:underline" data-testid={`view-site-${s.id}`}>
                  {t("sites.view")} →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
